// dwaar/src/pages/Saakshi.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Saakshi — Sanskrit साक्षी — witness.
//   The auth callback page. Witnesses a Supabase magic-link landing, exchanges
//   the token for a session, and either greets the user or shows the generic
//   public error.
//
// Two flows are supported:
//   Implicit: #access_token=...&refresh_token=...   (admin.generate_link — default today)
//   PKCE:    ?code=...                              (future-proof; handled cleanly)
//
// Reveal doctrine:
//   - Upstream error detail (Supabase, network) stays in the console.
//   - Only PUBLIC_ERROR is shown to the user.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getSupabaseClient } from "../lib/supabaseClient.js";
import { BRAND_NAME, PUBLIC_ERROR } from "../lib/brand.js";

const PHASE = Object.freeze({
  VERIFYING: "verifying",
  SUCCESS:   "success",
  ERROR:     "error",
});

/** Parse tokens from window.location. Pure helper — easy to reason about. */
export function readAuthParams(location = window.location) {
  const hash = (location.hash || "").replace(/^#/, "");
  const hashParams = new URLSearchParams(hash);
  const accessToken  = hashParams.get("access_token");
  const refreshToken = hashParams.get("refresh_token");

  const queryParams = new URLSearchParams(location.search || "");
  const code = queryParams.get("code");

  if (accessToken && refreshToken) {
    return { kind: "implicit", accessToken, refreshToken };
  }
  if (code) {
    return { kind: "pkce", code };
  }
  return { kind: "none" };
}

function stripTokensFromUrl() {
  if (typeof window === "undefined") return;
  // Preserve pathname only; drop search + hash.
  const cleanUrl = window.location.pathname;
  window.history.replaceState({}, "", cleanUrl);
}

export default function Saakshi() {
  const navigate = useNavigate();
  const [phase,     setPhase]     = useState(PHASE.VERIFYING);
  const [userEmail, setUserEmail] = useState("");
  const [destPath,  setDestPath]  = useState("/dashboard");
  const hasRunRef    = useRef(false);
  const cancelledRef = useRef(false);

  // Auto-navigate after a successful session exchange.
  // Destination: /supervisor for admin role, /dashboard for everyone else.
  // The 1500 ms grace period lets the user see the success state briefly.
  useEffect(() => {
    if (phase !== PHASE.SUCCESS) return;
    const timer = setTimeout(() => navigate(destPath, { replace: true }), 1500);
    return () => clearTimeout(timer);
  }, [phase, navigate, destPath]);

  useEffect(() => {
    // StrictMode double-invokes effects in dev — guard so we only exchange once.
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    cancelledRef.current = false;

    const params = readAuthParams();

    if (params.kind === "none") {
      setPhase(PHASE.ERROR);
      return;
    }

    (async () => {
      try {
        const client = getSupabaseClient();
        let result;
        if (params.kind === "implicit") {
          result = await client.auth.setSession({
            access_token: params.accessToken,
            refresh_token: params.refreshToken,
          });
        } else {
          result = await client.auth.exchangeCodeForSession(params.code);
        }

        if (result?.error || !result?.data?.session) {
          // eslint-disable-next-line no-console
          console.error("saakshi: session exchange failed", result?.error);
          setPhase(PHASE.ERROR);
          return;
        }

        const session = result.data.session;
        setUserEmail(session.user?.email || "");

        // Role-based redirect: admin → /supervisor, customer → /dashboard.
        // Failure to fetch the user row is non-fatal — fall back to /dashboard.
        try {
          const { data: userRow } = await client
            .from("users")
            .select("role")
            .eq("id", session.user.id)
            .single();
          if (!cancelledRef.current) {
            if (userRow?.role === "admin") setDestPath("/supervisor");
            else if (userRow?.role === "auditor") setDestPath("/audit");
          }
        } catch {
          // eslint-disable-next-line no-console
          console.warn("saakshi: role lookup failed, defaulting to /dashboard");
        }

        if (cancelledRef.current) return;
        stripTokensFromUrl();
        setPhase(PHASE.SUCCESS);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("saakshi: unexpected error", err);
        setPhase(PHASE.ERROR);
      }
    })();
    return () => { cancelledRef.current = true; };
  }, []);

  return (
    <main className="tl-shell">
      <section className="tl-card" aria-labelledby="saakshi-heading">
        <a href="https://terralekha.in" className="tl-wordmark">{BRAND_NAME}</a>

        {phase === PHASE.VERIFYING && (
          <>
            <h1 id="saakshi-heading" className="tl-heading">
              Verifying sign-in
            </h1>
            <p className="tl-sub tl-loading">
              One moment while we confirm your sign-in link&hellip;
            </p>
          </>
        )}

        {phase === PHASE.SUCCESS && (
          <>
            <h1 id="saakshi-heading" className="tl-heading">
              Signed in
            </h1>
            <p className="tl-sub">
              Welcome{userEmail ? `, ${userEmail}` : ""}. Taking you to your
              {destPath === "/supervisor" ? " control centre"
               : destPath === "/audit"     ? " assurance dashboard"
               : " workspace"}&hellip;
            </p>
          </>
        )}

        {phase === PHASE.ERROR && (
          <>
            <h1 id="saakshi-heading" className="tl-heading">
              Sign-in link could not be verified
            </h1>
            <p className="tl-error" role="alert">
              {PUBLIC_ERROR}
            </p>
            <p className="tl-sub">
              If the link has expired, request a fresh one from the sign-in
              page.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
